import {
    DesktopCapturerSourceExt,
    ExtraVideoOptions,
    AudioSourceInfo,
    AudioInputSettings,
    AudioInputStreamInfo,
    AudioTrackWithInfo,
} from '../UserContext'
import { MIDIInput, MIDIOutput } from '../types/WebMidi'
import { NativeAudioDevice } from './ElectronIPC'
import { env } from './EnvironmentVariables'
import ElectronIPC from './ElectronIPC'
import { DesktopCapturerSource } from 'electron'
import { logger } from './Logger'
import { getAppSerialId } from '../actions/AppSerialId'
import { FeatureFlags } from './FeatureFlags'
import {
    desktop_source_thumbnail_size,
    system_audio_device_mapping,
    win32_muse_midi_output_device_label,
} from './Constants'
import { getAudioEngineInstance, AudioEngineAudioDeviceInfo } from '../core/AudioEngine'
import { audioMediaSourceMatcher } from '../Logic'
import { MatchedMediaDeviceInfo } from '../core/AudioMediaSourceMatcher'
import { persistent } from './LocalStorage'
import { LocalVideoTrack } from 'livekit-client'

const log = logger.createChildLogger({ name: 'MediaDevices' })

const validChannelSelectionCodes = ['1', '2', '1&2', '1-2'] as const
type ChannelSelectionCodeType = (typeof validChannelSelectionCodes)[number] | undefined

export class MediaDeviceError {
    name: string
    message: string
    mediaType: 'video' | 'audio' | 'screen'

    constructor(mediaType: 'video' | 'audio' | 'screen', e: any) {
        this.name = e.name
        this.message = e.message
        this.mediaType = mediaType
    }
}

export async function getDesktopSources(): Promise<DesktopCapturerSourceExt[]> {
    const desktop: DesktopCapturerSource[] = await ElectronIPC.getDesktopCapturerSources({
        types: ['window', 'screen'],
        thumbnailSize: desktop_source_thumbnail_size,
        fetchWindowIcons: false,
    })

    console.log(`getDesktopSources: ElectronIPC.getDesktopCapturerSources returned %O`, desktop)

    // Resolve each desktop of type=screen into DesktopCaptureSourceExt.
    // Especially, DesktopCaptureSourceExt contains an extra "scaleFactor" for screens.

    // Get scaleFactor for each screen via ElectronIPC.
    const displays = await ElectronIPC.getAllDisplays()

    if (env.TEST_GET_DESKTOP_SOURCES_NULL_DISPLAY_ID) {
        for (let d of desktop) {
            d.display_id = ''
        }
    }

    let currScreenIdx = 0
    const desktopExt = desktop.map((d) => {
        let scaleFactor = 1

        /**
         * id String - The identifier of a window or screen that can be used as a chromeMediaSourceId constraint
         * when calling [navigator.webkitGetUserMedia]. The format of the identifier will be window:XX:YY or screen:ZZ:0.
         * XX is the windowID/handle. YY is 1 for the current process, and 0 for all others. ZZ is a sequential number
         * that represents the screen, and it does not equal to the index in the source's name.
         */
        let screenIndex = null
        let screenWidth = null
        let screenHeight = null
        let screenPositionX = null
        let screenPositionY = null
        let nonScaledBounds = null
        if (d.id.startsWith('screen')) {
            let correspondingDisplay = displays.find((a) => a.id === parseInt(d.display_id)) || null
            if (!correspondingDisplay) {
                // Saw display_id=="" on some systems. See #1209. In that case, fall back to matching
                // by order.
                if (displays[currScreenIdx]) {
                    correspondingDisplay = displays[currScreenIdx] || null
                    log.info(
                        'getDesktopSources',
                        'falling back to order-based matching because corresponding display ' +
                            `for display_id="${d.display_id}" could not be found.`,
                        { displays, currScreenIdx, correspondingDisplay }
                    )
                }
            }

            if (correspondingDisplay) {
                screenIndex = currScreenIdx
                scaleFactor = correspondingDisplay.scaleFactor
                screenWidth = correspondingDisplay.bounds.width
                screenHeight = correspondingDisplay.bounds.height
                screenPositionX = correspondingDisplay.bounds.x
                screenPositionY = correspondingDisplay.bounds.y
                nonScaledBounds = correspondingDisplay.nonScaledBounds
            } else {
                log.error(
                    'getDesktopSources',
                    'getDesktopSources: could not find the corresponding display ' +
                        `for screenIdx=${currScreenIdx} in displays[] of length ${displays.length}.`,
                    { displays, currScreenIdx }
                )
            }

            currScreenIdx += 1
        }

        const r: DesktopCapturerSourceExt = {
            ...d,
            screenIndex: screenIndex,
            scaleFactor: scaleFactor,
            screenWidth: screenWidth,
            screenHeight: screenHeight,
            screenPositionX: screenPositionX,
            screenPositionY: screenPositionY,
            nonScaledBounds: nonScaledBounds,
        }
        return r
    })

    console.log(`getDesktopSources: resolved %O`, desktopExt)

    return desktopExt
}

async function enumerateDevicesSafe() {
    let sources: MediaDeviceInfo[] = []
    try {
        sources = (await navigator.mediaDevices.enumerateDevices()) as MediaDeviceInfo[]
    } catch (e) {
        // TODO: log
        console.error(e)
    }

    return sources
}

interface getAvailableAVMediaSourcesReturn {
    video: MediaDeviceInfo[]
    virtualCamera: MediaDeviceInfo | null
    audioInputs: AudioSourceInfo[]
    audioOutputs: AudioSourceInfo[]
    audioOutputsAll: AudioSourceInfo[]
}

function isSameDeviceHeuristic(a: MediaDeviceInfo, b: MediaDeviceInfo) {
    let result = false
    if (a.groupId !== b.groupId) {
        // If the groupIds do not match, then we know immediately that they are separate devices.
        result = false
    }
    if (a.deviceId === 'default' && a.label.replace('Default - ', '') === b.label) {
        result = true
    }
    if (b.deviceId === 'default' && b.label.replace('Default - ', '') === a.label) {
        result = true
    }
    if (a.deviceId === 'communications' && a.label.replace('Communications - ', '') === b.label) {
        result = true
    }
    if (b.deviceId === 'communications' && b.label.replace('Communications - ', '') === a.label) {
        result = true
    }

    log.isSilly() &&
        log.silly(
            'isSameDeviceHeuristic',
            `a=(groupId="${a.groupId}" deviceId="${a.deviceId}"), ` +
                `b=("${b.groupId}", "${b.deviceId}") result=${result}`
        )

    return result
}

async function classifySourcesByDeviceId(sources: MediaDeviceInfo[]) {
    let audioInputs: MediaDeviceInfo[] = []
    let audioOutputs: MediaDeviceInfo[] = []
    let audioOutputsAll: MediaDeviceInfo[] = []
    let video: MediaDeviceInfo[] = []

    // classify
    sources.forEach((source: MediaDeviceInfo) => {
        if (source.kind === 'audioinput') {
            // NOTE: notice we use the deviceId as the key.
            audioInputs.push(source)
        } else if (source.kind === 'videoinput') {
            video.push(source)
        } else if (source.kind === 'audiooutput') {
            if (!isSystemAudio(source)) {
                audioOutputs.push(source)
            }
            audioOutputsAll.push(source)
        } else {
            console.error(`Unrecognized media source kind "${source.kind}".`)
        }
    })

    return {
        audioInputs,
        audioOutputs,
        audioOutputsAll,
        video,
    }
}

async function classifySourcesWithMergeHeuristics(sources: MediaDeviceInfo[]) {
    let { audioInputs, audioOutputs, audioOutputsAll, video } = await classifySourcesByDeviceId(sources)

    // classify
    sources.forEach((source: MediaDeviceInfo) => {
        if (source.kind === 'audioinput') {
            if (source.deviceId !== 'default' && source.deviceId !== 'communications') {
                // Check audioInputs list for duplicate devices that we can drop.
                audioInputs = audioInputs.filter((a) => !isSameDeviceHeuristic(a, source))
            }
        } else if (source.kind === 'videoinput') {
            if (video.filter((v) => v.deviceId === source.deviceId).length === 0) {
                video.push(source)
            }
        } else if (source.kind === 'audiooutput') {
            // NOTE: handling `audioOutputs`
            if (!isSystemAudio(source)) {
                if (source.deviceId !== 'default' && source.deviceId !== 'communications') {
                    // Check audioOutputs list for duplicate devices that we can drop.
                    audioOutputs = audioOutputs.filter((a) => !isSameDeviceHeuristic(a, source))
                }
            }

            // NOTE: handling `audioOutputsAll`
            if (source.deviceId !== 'default' && source.deviceId !== 'communications') {
                // Check audioOutputs list for duplicate devices that we can drop.
                audioOutputsAll = audioOutputsAll.filter((a) => !isSameDeviceHeuristic(a, source))
            }
        } else {
            console.error(`Unrecognized media source kind "${source.kind}".`)
        }
    })

    return {
        audioInputs,
        audioOutputs,
        audioOutputsAll,
        video,
    }
}

function mkAsioOutputAudioSourceInfo(asioAudioEngineDevices: AudioEngineAudioDeviceInfo[]) {
    return asioAudioEngineDevices
        .filter((a) => a.maxOutputChannels > 0)
        .map((a) => {
            return {
                deviceId: a.audioEngineDeviceId,
                groupId: '',
                kind: 'audiooutput',
                label: a.name,
                nativeDeviceId: null,
                audioEngineDeviceId: a.audioEngineDeviceId,
                deviceUid: null,
                displayLabel: a.name,
                defaultSampleRate: a.defaultSampleRate,
                maxInputChannels: a.maxInputChannels,
                maxOutputChannels: a.maxOutputChannels,
                hostApiName: a.hostApiName,
                isSystemAudio: false,
                isMuseAudioShare: false,
                isMuseDAWBridge: false,
            } as AudioSourceInfo
        })
}

function mkAsioInputAudioSourceInfo(asioAudioEngineDevices: AudioEngineAudioDeviceInfo[]) {
    return asioAudioEngineDevices
        .filter((a) => a.maxInputChannels > 0)
        .map((a) => {
            return {
                deviceId: a.audioEngineDeviceId,
                groupId: '',
                kind: 'audioinput',
                label: a.name,
                nativeDeviceId: null,
                audioEngineDeviceId: a.audioEngineDeviceId,
                deviceUid: null,
                displayLabel: a.name,
                defaultSampleRate: a.defaultSampleRate,
                maxInputChannels: a.maxInputChannels,
                maxOutputChannels: a.maxOutputChannels,
                hostApiName: a.hostApiName,
                isSystemAudio: false,
                isMuseAudioShare: false,
                isMuseDAWBridge: false,
            } as AudioSourceInfo
        })
}

function mkAudioSourceInfo(audioDevice: MatchedMediaDeviceInfo): AudioSourceInfo {
    // @ts-ignore - force cast
    let audioDeviceExt: AudioSourceInfo = {
        deviceId: audioDevice.deviceId,
        groupId: audioDevice.groupId,
        kind: audioDevice.kind,
        label: audioDevice.label,
    }

    // set native device related fields
    const nativeDevice = audioDevice.nativeDevice || null
    if (nativeDevice) {
        // Set nativeDeviceId
        if (nativeDevice.deviceId) {
            audioDeviceExt.nativeDeviceId = nativeDevice.deviceId + '' // stringify
        } else {
            audioDeviceExt.nativeDeviceId = null
        }

        // Set deviceUid
        audioDeviceExt.deviceUid = nativeDevice.deviceUid
    } else {
        audioDeviceExt.nativeDeviceId = null
        audioDeviceExt.deviceUid = null
    }

    // set audioengine related fields
    const audioEngineDevice = audioDevice.audioEngineDevice || null
    if (audioEngineDevice) {
        audioDeviceExt.audioEngineDeviceId = audioEngineDevice.audioEngineDeviceId
        audioDeviceExt.defaultSampleRate = audioEngineDevice.defaultSampleRate
        audioDeviceExt.maxInputChannels = audioEngineDevice.maxInputChannels
        audioDeviceExt.maxOutputChannels = audioEngineDevice.maxOutputChannels
        audioDeviceExt.hostApiName = audioEngineDevice.hostApiName
    } else {
        // @ts-ignore
        if (typeof audioDevice.getCapabilities === 'function') {
            // @ts-ignore
            const caps = audioDevice.getCapabilities() as MediaTrackCapabilities
            if (caps.channelCount && caps.channelCount.max) {
                audioDeviceExt.maxInputChannels = caps.channelCount.max
            }
        }
        audioDeviceExt.audioEngineDeviceId = null
    }

    audioDeviceExt.isSystemAudio = isSystemAudio(audioDeviceExt)
    audioDeviceExt.isMuseAudioShare = isMuseAudioShareInputDevice(audioDeviceExt)
    audioDeviceExt.isMuseDAWBridge = isMuseDAWBridgeDevice(audioDeviceExt)

    // set `displayLabel`
    if (isMuseDAWBridgeDevice(audioDeviceExt)) {
        // audioInputExt.displayLabel = 'DAW Audio (Muse DAW Bridge)' // audioInput.label.replace('(Virtual)', '(DAW Audio)')

        if (env.OS === 'darwin') {
            const labelWithoutParens = audioDevice.label.replace('(Virtual)', '').trim()
            audioDeviceExt.displayLabel = `Muse Plugin Audio (${labelWithoutParens})`
        } else {
            audioDeviceExt.displayLabel = audioDevice.label.replace('(Virtual)', '(Plugin Audio)')
        }
    } else if (isSystemAudio(audioDevice)) {
        audioDeviceExt.displayLabel = 'Computer Audio (Muse Audio Share)'

        if (env.OS === 'darwin') {
            const labelWithoutParens = audioDevice.label.replace('(Virtual)', '').trim()
            audioDeviceExt.displayLabel = `Computer Audio (${labelWithoutParens})`
        } else {
            audioDeviceExt.displayLabel = audioDevice.label.replace('(Virtual)', '(System Audio)')
        }
    } else {
        audioDeviceExt.displayLabel = audioDevice.label
    }

    return audioDeviceExt
}

function isVirtualCamera(v: MediaDeviceInfo) {
    return v.label && v.label.indexOf('esum Virtual') >= 0
}

export async function triggerPermissionPromptOnWebapp() {
    if (!env.IS_ELECTRON) {
        try {
            /* @ts-ignore The typescript types are outdated? */
            const camPerm = await navigator.permissions.query({ name: 'camera' })
            /* @ts-ignore The typescript types are outdated? */
            const micPerm = await navigator.permissions.query({ name: 'microphone' })
            if (camPerm.state !== 'granted' || micPerm.state !== 'granted') {
                const media = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
                log.info('triggerPermissionPromptOnWebapp', `getUserMedia found ${media.getTracks().length} tracks`)
            }
            return true
        } catch (e) {
            if (e instanceof Error) {
                if (e.name === 'NotAllowedError') {
                    return false
                }
            }
            log.info(
                'triggerPermissionPromptOnWebapp',
                'Encountered a device error while triggering permissions popup.',
                { err: e }
            )
            return true
        }
    }
}

export async function getAudioAndVideoMediaSources({
    forceShowAllAudioDevices,
    origin,
    timeoutMs = 8000,
}: {
    forceShowAllAudioDevices: boolean
    origin: string
    timeoutMs?: number
}): Promise<getAvailableAVMediaSourcesReturn> {
    log.checkpoint('get_audio_sources_called', '', { forceShowAllAudioDevices, origin, timeoutMs })

    let sources: MediaDeviceInfo[] = []
    let nativeAudioDevices: NativeAudioDevice[] = []
    let audioEngineDevices: AudioEngineAudioDeviceInfo[] = []

    // trigger permission prompt if webapp (!env.IS_ELECTRON)
    await triggerPermissionPromptOnWebapp()

    // get device list
    let timerStart = new Date()
    if (env.IS_ELECTRON) {
        const r = await Promise.all([
            enumerateDevicesSafe(),
            // NOTE: we have experienced this call taking a long time (~10s)
            // on slow machines, especially right after app startup.
            ElectronIPC.getNativeAudioDevicesList({ timeoutMs }),
            // returns null if error.
            getAudioEngineInstance().getAudioDevices(),
        ])
        sources = r[0]
        nativeAudioDevices = r[1]
        audioEngineDevices = r[2] || []
    } else {
        sources = await enumerateDevicesSafe()
    }

    // classify `sources` into audioInputs, audioOutputs, audioOutputsAll, and video
    // while merging duplicate listings.
    let r: {
        audioInputs: MediaDeviceInfo[]
        audioOutputs: MediaDeviceInfo[]
        audioOutputsAll: MediaDeviceInfo[]
        video: MediaDeviceInfo[]
    } = { audioInputs: [], audioOutputs: [], audioOutputsAll: [], video: [] }

    const hasOutput = sources.find((s) => s.kind === 'audiooutput') !== undefined
    if (!hasOutput) {
        sources.push({
            deviceId: 'default',
            groupId: 'default',
            kind: 'audiooutput',
            label: 'Default Speaker',
        } as MediaDeviceInfo)
    }

    if (forceShowAllAudioDevices) {
        r = await classifySourcesByDeviceId(sources)
        console.log('getAvailableAVMediaSources: classifySourcesByDeviceId returned %O', { r })
    } else {
        r = await classifySourcesWithMergeHeuristics(sources)
        console.log('getAvailableAVMediaSources: classifySourcesWithMergeHeuristics returned %O', { r })
    }

    // match Chrome audio sources with nativeAudioDevices and audioEngineDevices.
    let audioInputsMatched: MatchedMediaDeviceInfo[]
    let audioOutputsMatched: MatchedMediaDeviceInfo[]
    let audioOutputsAllMatched: MatchedMediaDeviceInfo[]

    let nonAsioAudioEngineDevices = audioEngineDevices.filter((a) => a.hostApiName !== 'ASIO')
    let asioAudioEngineDevices = audioEngineDevices.filter((a) => a.hostApiName === 'ASIO')

    if (env.IS_ELECTRON) {
        // we use `nonAsioAudioEngineDevices` because recall that Chrome cannot list ASIO devices.
        let r1 = audioMediaSourceMatcher.run(
            r.audioInputs,
            nativeAudioDevices.filter((a) => a.isInput ?? true),
            nonAsioAudioEngineDevices.filter((a) => a.maxInputChannels > 0)
        )
        audioInputsMatched = r1[0]

        let r2 = audioMediaSourceMatcher.run(
            r.audioOutputs,
            nativeAudioDevices.filter((a) => a.isOutput ?? true),
            nonAsioAudioEngineDevices.filter((a) => a.maxOutputChannels > 0)
        )
        audioOutputsMatched = r2[0]

        let r3 = audioMediaSourceMatcher.run(
            r.audioOutputsAll,
            nativeAudioDevices.filter((a) => a.isOutput ?? true),
            nonAsioAudioEngineDevices.filter((a) => a.maxOutputChannels > 0)
        )
        audioOutputsAllMatched = r3[0]
    } else {
        audioInputsMatched = r.audioInputs.map((a) => {
            let b = a as MatchedMediaDeviceInfo
            b.nativeDevice = null
            b.audioEngineDevice = null
            return b
        })
        audioOutputsMatched = r.audioOutputs.map((a) => {
            let b = a as MatchedMediaDeviceInfo
            b.nativeDevice = null
            b.audioEngineDevice = null
            return b
        })
        audioOutputsAllMatched = r.audioOutputsAll.map((a) => {
            let b = a as MatchedMediaDeviceInfo
            b.nativeDevice = null
            b.audioEngineDevice = null
            return b
        })
    }

    let audioInputs: AudioSourceInfo[] = audioInputsMatched.map((a) => mkAudioSourceInfo(a))
    let audioOutputs: AudioSourceInfo[] = audioOutputsMatched.map((a) => mkAudioSourceInfo(a))
    let audioOutputsAll: AudioSourceInfo[] = audioOutputsAllMatched.map((a) => mkAudioSourceInfo(a))

    // for windows audioengine: also check the asio case, where the device exists
    // in `audioEngineDevices` but not in the list of audio devices returned by Chromium.
    audioInputs = audioInputs.concat(mkAsioInputAudioSourceInfo(asioAudioEngineDevices))
    let asioOutputAudioSourceInfos = mkAsioOutputAudioSourceInfo(asioAudioEngineDevices)
    audioOutputs = audioOutputs.concat(asioOutputAudioSourceInfos)
    audioOutputsAll = audioOutputsAll.concat(asioOutputAudioSourceInfos)

    // set matchingLoopbackSource for system audio loopback devices (i.e. VB Audio Cable Input/Output pairs)
    audioInputs.forEach((a) => {
        const devices = system_audio_device_mapping.filter((b) => a.label.indexOf(b.outputDeviceLabel) >= 0)
        if (devices.length > 0) {
            const match = audioOutputsAll.find((b) => {
                for (let device of devices) {
                    if (b.label.indexOf(device.inputDeviceLabel) >= 0) {
                        return true
                    }
                }
                return false
            })
            if (match) {
                a.matchingLoopbackSource = match
                // console.log(`**** matching "${a.label}" with "${match.label}".`)
            }
        }
    })

    let elapsedMs = new Date().getTime() - timerStart.getTime()
    log.checkpoint('got_audio_sources', '', {
        audioInputs,
        audioOutputs,
        audioEngineDevices,
        nativeAudioDevices,
        elapsedMs,
    })

    if (FeatureFlags.isEnabled('browser-default-speaker-output-only')) {
        audioOutputs = [
            {
                deviceId: 'default',
                groupId: 'default',
                kind: 'audiooutput',
                label: 'Default Speaker',
                nativeDeviceId: '',
                audioEngineDeviceId: '',
                deviceUid: '',
                displayLabel: '',
                defaultSampleRate: 44100,
                maxInputChannels: 0,
                maxOutputChannels: 0,
                hostApiName: '',
                isSystemAudio: false,
                isMuseAudioShare: false,
                isMuseDAWBridge: false,
            },
        ]
    }

    // Among the video (camera) sources, we separate out the virtual camera.
    const video: MediaDeviceInfo[] = r.video.filter((v) => !isVirtualCamera(v))
    const virtualCamera: MediaDeviceInfo | null = r.video.filter((v) => isVirtualCamera(v))[0] || 0

    if (!getAppSerialId()) {
        throw new Error(`Expected "env.APP_SERIAL_ID" to be defined!`)
    }

    // for Windows, duplicate VB Audio Cable, one for System Audio and another for DAW Bridge.
    if (env.OS === 'win32') {
        const vbAudioCable = audioInputs.find((a) => isVbAudioCable(a))
        if (vbAudioCable) {
            audioInputs.push({
                // NOTE(WindowsInternalMuseDawBridgeGroupIdWorkaround): will encode the original
                // `vbAudioCable.deviceId` into the deviceId since it's being overwritten.
                deviceId: `__internal_muse_daw_bridge:${env.APP_SERIAL_ID.split('-')[0]}:${vbAudioCable.deviceId}`,
                nativeDeviceId: null,
                deviceUid: null,
                groupId: vbAudioCable.groupId,
                kind: 'audioinput',
                label: 'VB-Audio Virtual Cable (Muse Plugin)',
                displayLabel: 'VB-Audio Virtual Cable (Muse Plugin)',
                audioEngineDeviceId: vbAudioCable.audioEngineDeviceId,
                defaultSampleRate: vbAudioCable.defaultSampleRate,
                maxInputChannels: vbAudioCable.maxInputChannels,
                maxOutputChannels: vbAudioCable.maxOutputChannels,
                hostApiName: vbAudioCable.hostApiName,
                isSystemAudio: true,
                isMuseAudioShare: false,
                isMuseDAWBridge: true,
            })
        }
    }

    if (env['FEATURE_CHROME_SYSTEM_AUDIO']) {
        if (env.OS !== 'darwin') {
            audioInputs.push({
                deviceId: '__internal_muse_system_audio',
                nativeDeviceId: null,
                deviceUid: null,
                groupId: '__internal_muse_system_audio',
                kind: 'audioinput',
                label: 'Muse System Audio',
                displayLabel: 'Muse System Audio',
                audioEngineDeviceId: null,
                defaultSampleRate: 0,
                maxInputChannels: 0,
                maxOutputChannels: 0,
                isSystemAudio: false,
                isMuseAudioShare: false,
                isMuseDAWBridge: false,
                hostApiName: '',
            })
        }
    }
    if (env['MOCK_MEDIA_DEVICES_LIST']) {
        for (let i = 0; i < 10; ++i) {
            audioInputs.push({
                deviceId: '__mocked_device_' + i,
                nativeDeviceId: null,
                deviceUid: null,
                groupId: '__mocked_device_' + i,
                kind: 'audioinput',
                label: 'Mocked device ' + i,
                displayLabel: 'Mocked device ' + i,
                audioEngineDeviceId: null,
                defaultSampleRate: 0,
                maxInputChannels: 0,
                maxOutputChannels: 0,
                isSystemAudio: false,
                isMuseAudioShare: false,
                isMuseDAWBridge: false,
                hostApiName: '',
            })
        }
    }
    if (env['MOCK_NO_EXTERNAL_AUDIO']) {
        audioInputs = audioInputs.filter((dev) => isSystemAudio(dev))
    }
    if (env['MOCK_NO_SYSTEM_AUDIO']) {
        audioInputs = audioInputs.filter((dev) => !isSystemAudio(dev))
    }
    if (env.TEST_ASIO_DEVICE_WITH_WEBRTC_SETTING) {
        audioInputs.push({
            deviceId: '__TEST_ASIO_DEVICE_WITH_WEBRTC_SETTING_mocked_asio_device',
            nativeDeviceId: 'Mocked ASIO device',
            deviceUid: null,
            groupId: '__mocked_asio_device',
            kind: 'audioinput',
            label: 'Mocked ASIO device',
            displayLabel: 'Mocked ASIO device',
            audioEngineDeviceId: 'Mocked ASIO device',
            defaultSampleRate: 0,
            maxInputChannels: 0,
            maxOutputChannels: 0,
            isSystemAudio: false,
            isMuseAudioShare: false,
            isMuseDAWBridge: false,
            hostApiName: 'ASIO',
        })
        persistent.set('AudioSettingsStore::AudioInputDevice::Mocked ASIO device', {
            sampleRate: 32000,
            bitDepth: 16,
            channelSelection: [[0, 1]],
            isCompressed: true,
            compressionBitrate: 192000,
            isAudioEngine: false,
            isHighQuality: false,
            receiverBufferSizeMs: 250,
            receiverBufferMaxDriftMs: 0,
            framesPerBuffer: 0,
            networkPacketDurationMs: 0,
            enableLocalLoopback: false,
            isVoiceQuality: false,
        })
    }

    console.log('getAvailableAVMediaSources: returning audioInputs=%O', audioInputs)

    return { video, virtualCamera, audioInputs, audioOutputs, audioOutputsAll }
}

export async function getDefaultAudioOutputType() {
    let sources: MediaDeviceInfo[] = []
    try {
        sources = (await navigator.mediaDevices.enumerateDevices()) as MediaDeviceInfo[]
    } catch (e) {
        // TODO: log
        console.error(e)
    }

    const selectedOutput = sources.filter((v) => v.kind === 'audiooutput')[0]
    return {
        isInternal: isSystemAudio(selectedOutput),
        isBuiltInLoopback: isBuiltInLoopback(selectedOutput),
    }
}

export async function getMidiDevices() {
    const midiInputs: MIDIInput[] = []
    const midiOutputs: MIDIOutput[] = []

    try {
        // @ts-ignore
        const access: MIDIAccess = await navigator.requestMIDIAccess()

        // @ts-ignore
        access.inputs.forEach((port: MIDIInput) => {
            if (port.name === 'Muse DAW Controller' || port.name === win32_muse_midi_output_device_label) {
                return
            }
            midiInputs.push(port)
        })

        // @ts-ignore
        access.outputs.forEach((port: MIDIOutput) => {
            midiOutputs.push(port)
        })

        // console.log(`requestMIDIAccess: inputPorts=%O outputPorts=%O`, midiInputs, midiOutputs)
        return { midiInputs, midiOutputs }
    } catch (e) {
        // requestMIDIAccess may fail on Linux with DOMException: Platform dependent initialization failed.
        console.error(`Not loading MIDI because navigator.requestMIDIAccess() returned error: %O`, e)
        return { midiInputs, midiOutputs }
    }
}

export type DesktopCaptureOptions = {
    maxFrameRate?: number | null
    maxWidth?: number | null
}

export async function desktopMediaSourceToStream(
    source: DesktopCapturerSourceExt,
    desktopCaptureOptions: ExtraVideoOptions
): Promise<MediaStream | MediaDeviceError | null> {
    if (source == null) {
        return null
    }

    const id = source.id
    if (!id || id === '_none' || id === '_undef') {
        return null
    }

    try {
        const stream = (await (<any>navigator.mediaDevices).getUserMedia({
            audio: false,
            video: {
                mandatory: {
                    chromeMediaSource: 'desktop',
                    chromeMediaSourceId: id,
                    // ...options
                },
            },
        })) as MediaStream

        const vtrack = stream.getVideoTracks()[0] || null
        if (vtrack instanceof MediaStreamTrack === false) {
            log.assert('desktopMediaSourceToStream', `stream contains no video track!`)
            return new MediaDeviceError('screen', { name: 'no_video_track_found', message: '' })
        }

        try {
            const v = vtrack.getSettings()
            const options: MediaTrackConstraints = {}

            const cap = vtrack.getCapabilities()

            if (desktopCaptureOptions.optionHeight != null) {
                if ((v.height || 0) > desktopCaptureOptions.optionHeight) {
                    options.height = desktopCaptureOptions.optionHeight
                }
            }
            if (desktopCaptureOptions.optionFramerate != null) {
                options.frameRate = desktopCaptureOptions.optionFramerate
            }

            if (Object.keys(desktopCaptureOptions).length > 0) {
                await vtrack.applyConstraints(options)
            }
        } catch (e) {
            log.assert('desktopMediaSourceToStream', `an exception occurred while applying constraints.`, { e })
        }

        return stream
    } catch (e) {
        log.error('desktopMediaSourceToStream', `an exception occurred while getting media.`, { e })
        // available error codes (e.name) at <https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia>
        return new MediaDeviceError('screen', e)
    }
}

type Options = {
    width?: number
    height?: number
    scalePercentage?: number
    frameRate?: number
    aspectRatio?: number
}

export async function getVideoStreamFromId(
    id: string,
    options?: Options
): Promise<LocalVideoTrack | MediaDeviceError | null> {
    log.info('getVideoStreamFromId', `called with options=`, { options })

    if (id === '_none' || id === '_undef') {
        return null
    }

    try {
        const constraints: MediaStreamConstraints = {
            video: {
                deviceId: { exact: id },
            },
        }

        // Fill in the conditional constraints
        if (options?.width) {
            // @ts-ignore
            constraints.video.width = { ideal: options?.width }
        }
        if (options?.height) {
            // @ts-ignore
            constraints.video.height = { ideal: options?.height }
        }
        if (options?.frameRate) {
            // @ts-ignore
            constraints.video.frameRate = { ideal: options.frameRate }
        }
        if (options?.aspectRatio) {
            // @ts-ignore
            constraints.video.aspectRatio = { ideal: options.aspectRatio }
        }

        log.info('getVideoStreamFromId', `calling getUserMedia() with constraints=`, { constraints })

        // Actually get the video stream
        const s = await navigator.mediaDevices.getUserMedia(constraints)
        const mediaStreamTrack = s.getVideoTracks()[0] || null
        const videoTrack = mediaStreamTrack ? new LocalVideoTrack(mediaStreamTrack) : null

        if (!videoTrack) return null

        log.info('getVideoStreamFromId', `getUserMedia resolved=`, {
            settings: videoTrack.mediaStreamTrack.getSettings(),
        })

        // apply scalePercentage; we do this after getting the media since we need
        // to know the original dimension.
        if (options && Object.keys(options).length > 0) {
            const scale = options.scalePercentage

            const currentSettings = videoTrack.mediaStreamTrack.getSettings()
            const newWidth = Math.round((currentSettings.width || 0) * (scale || 0)) || 0
            const newHeight = (currentSettings.height || 0) * (scale || 0)

            const constraints: MediaTrackConstraints = {}
            if (newWidth > 0 && newHeight > 0) {
                constraints.width = { ideal: newWidth }
                constraints.height = { ideal: Math.floor(newHeight) }
                if (options?.frameRate) {
                    constraints.frameRate = { ideal: options.frameRate }
                }

                try {
                    log.info('getVideoStreamFromId', `calling applyConstraints() with constraints=`, { constraints })
                    await videoTrack.mediaStreamTrack.applyConstraints(constraints)
                } catch (e) {}
            }
        }

        const c = videoTrack.mediaStreamTrack.getCapabilities()
        log.info('getVideoStreamFromId', `track capabilities=`, { capabilities: c })

        return videoTrack
    } catch (e) {
        // available error codes (e.name) at <https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia>
        return new MediaDeviceError('video', e)
    }
}

export async function getSystemAudioStream(): Promise<MediaStream> {
    // https://www.electronjs.org/docs/api/desktop-capturer
    // "navigator.mediaDevices.getUserMedia does not work on macOS for audio capture
    // due to a fundamental limitation whereby apps that want to access the system's audio
    // require a signed kernel extension. Chromium, and by extension Electron, does not
    // provide this.
    // "It is possible to circumvent this limitation by capturing system audio with another
    // macOS app like Soundflower and passing it through a virtual audio input device. This
    // virtual device can then be queried with navigator.mediaDevices.getUserMedia.

    const constraints = {
        audio: {
            mandatory: {
                chromeMediaSource: 'desktop',
            },
        },
        video: {
            mandatory: {
                chromeMediaSource: 'desktop',
            },
        },
    }
    const stream = await (<any>navigator.mediaDevices).getUserMedia(constraints)

    const atracks: MediaStreamTrack[] = []
    if (stream instanceof MediaStream) {
        stream.getAudioTracks().forEach((atrack) => {
            console.log('got atrack %O', atrack)
            atracks.push(atrack)
        })
        stream.getVideoTracks().forEach((vtrack) => {
            console.log('got vtrack %O', vtrack)
        })
    }

    return new MediaStream(atracks)
}

async function getAudioTrackHelper(
    source: AudioSourceInfo,
    settings: AudioInputSettings,
    constraints: MediaStreamConstraints
): Promise<AudioTrackWithInfo> {
    log.verbose('getAudioTrackHelper', `calling navigator.mediaDevices.getUserMedia() with params=`, {
        params: constraints,
    })
    const media = await navigator.mediaDevices.getUserMedia(constraints)
    let track = media.getAudioTracks()[0] || null

    if (!track) {
        throw new Error(`No audio tracks found from deviceId="${source.deviceId}".`)
    }

    if (settings.isHighQuality && settings.channelSelection.flat().length === 1) {
        const monoChannel = settings.channelSelection.flat()[0]
        track = await createTrackForMonoChannel(track, monoChannel)
    }

    //// let reportedChannelCountOverride = null
    //// // @ts-ignore
    //// if (desc.channelType === 'mono' && (track.getSettings().channelCount||0) >= 2) {
    ////     track = duplicateMediaStreamMonoChannelToStereo(track, desc.channelSelectionCodeLegacy)
    ////     reportedChannelCountOverride = 1
    //// }

    const s = track.getSettings()

    const info: AudioInputStreamInfo = {
        deviceId: source.deviceId,
        groupId: source.groupId,
        nativeDeviceId: source.nativeDeviceId,
        audioEngineDeviceId: null,
        audioEngineStreamUuid: null,

        label: source.label,
        displayLabel: source.displayLabel,

        isSystemAudio: source.isSystemAudio,
        isMuseAudioShare: source.isMuseAudioShare,
        isMuseDAWBridge: source.isMuseDAWBridge,

        channelCount: settings.channelSelection.flat().length,

        // values copied from AudioInputSettings:
        sampleRate: s?.sampleRate || settings.sampleRate,
        bitDepth: s?.sampleSize || settings.bitDepth,
        enableLocalLoopback: settings.enableLocalLoopback,
        isHighQuality: settings.isHighQuality,
        channelSelection: settings.channelSelection,
        isVoiceQuality: settings.isVoiceQuality,

        // unused values since not an audio engine stream.
        isAudioEngine: false,
        isCompressed: false,
        compressionBitrate: 0,
        receiverBufferSizeMs: 0,
        receiverBufferMaxDriftMs: 0,
        framesPerBuffer: 0,
        networkPacketDurationMs: 0,
        channelPackingMap: [],
        channelUnpackingMap: [],
    }
    let atrack: AudioTrackWithInfo = Object.assign(track, { info })

    return atrack
}

export async function getAudioTrack(
    source: AudioSourceInfo,
    settings: AudioInputSettings,
    options: { preventVolumeAutoAdjustment: boolean }
): Promise<AudioTrackWithInfo> {
    log.verbose('getAudioTrack', `start; desc=`, { desc: settings })

    let deviceId = source.deviceId
    if (env['TRIGGER_AUDIO_DEVICE_ERROR']) {
        deviceId = 'x'
    }

    if (source.deviceId === '__internal_muse_system_audio') {
        throw new Error('Not implemented')
        // return await getSystemAudioStream()
    }

    if (source.deviceId.startsWith('__internal_muse_daw_bridge')) {
        // see NOTE(WindowsInternalMuseDawBridgeGroupIdWorkaround).
        let tokens = source.deviceId.split(':')
        deviceId = tokens[tokens.length - 1] || ''
        if (!deviceId) {
            throw new Error(
                'Contract error: __internal_muse_daw_bridge is not properly enocded! ' +
                    'See WindowsInternalMuseDawBridgeGroupIdWorkaround.'
            )
        }
    }

    if (settings.isHighQuality) {
        const maxChannel = settings.channelSelection.flat().reduceRight((p, c) => Math.max(p, c))
        const channelCount = maxChannel + 1
        const params: MediaStreamConstraints = {
            // courtesy: https://github.com/higuma/web-audio-recorder-js/issues/16
            audio: {
                // @ts-ignore
                optional: [
                    { sourceId: deviceId },

                    { channelCount: Math.max(channelCount, 2) },
                    { sampleRate: { ideal: settings.sampleRate } },
                    { sampleSize: { ideal: settings.bitDepth } },

                    { googAutoGainControl: false },
                    { googAutoGainControl2: false },
                    { echoCancellation: false },
                    { googEchoCancellation: false },
                    { googEchoCancellation2: false },
                    { googDAEchoCancellation: false },
                    { googNoiseSuppression: false },
                    { googNoiseSuppression2: false },
                    { googHighpassFilter: false },
                    { googAudioMirroring: false },
                ],
            },
        }

        return await getAudioTrackHelper(source, settings, params)
    } else {
        const params: MediaStreamConstraints = {
            audio: {
                deviceId: { exact: deviceId },
                autoGainControl: !options?.preventVolumeAutoAdjustment,
            },
        }

        return await getAudioTrackHelper(source, settings, params)
    }
}

export function isExclusivelyAudioengine(source: AudioSourceInfo) {
    return !source.groupId
}

export function isSystemAudio(media: { label: string }) {
    if (env.TEST_LOAD_SYSTEM_AUDIO_AS_NORMAL) {
        return false
    }

    // If it is a media with a built-in loopback, then it is by definition an internal audio.
    if (isBuiltInLoopback(media)) {
        return true
    }

    // Note: not matching with startsWith because the OS may add stuff to the front
    // of the string, e.g. "Default - Muse 16ch".
    return (
        media.label.match('Muse ') != null ||
        (media.label.match('VB-Audio') != null && media.label.match('CABLE') != null) ||
        (media.label.match('Line') != null && media.label.match('Virtual Audio Cable') != null) ||
        isVbAudioVoiceMeeterVaio(media) ||
        isVbAudioHiFiCable(media) ||
        isVoicemeeterB(media)
    )
}
export function isVbAudioCable(media: { label: string }) {
    return (
        media.label.match('VB-Audio') != null &&
        media.label.match('CABLE') != null &&
        media.label.match('CABLE-A') == null && // filter out "CABLE-A Output", etc.
        media.label.match('CABLE-B') == null &&
        media.label.match('CABLE-C') == null &&
        media.label.match('CABLE-D') == null
    )
}

export function isVbAudioCableA(media: { label: string }) {
    return media.label.match('VB-Audio') != null && media.label.match('CABLE-A') != null
}

export function isVbAudioCableB(media: { label: string }) {
    return media.label.match('VB-Audio') != null && media.label.match('CABLE-B') != null
}

export function isVbAudioCableC(media: { label: string }) {
    return media.label.match('VB-Audio') != null && media.label.match('CABLE-C') != null
}

export function isVbAudioCableD(media: { label: string }) {
    return media.label.match('VB-Audio') != null && media.label.match('CABLE-D') != null
}

export function isVirtualAudioCable(media: { label: string }) {
    return media.label.match('Line 1') != null && media.label.match('Virtual Audio Cable') != null
}

export function isVbAudioVoiceMeeterVaio(media: { label: string }) {
    return media.label.match('VoiceMeeter') != null && media.label.match('VB-Audio VoiceMeeter VAIO') != null
}

export function isVbAudioHiFiCable(media: { label: string }) {
    return media.label.match('Hi-Fi Cable') != null && media.label.match('VB-Audio Hi-Fi Cable') != null
}

export function isMuseAudioShareInputDevice(media: { label: string; deviceId: string }) {
    // on Mac, match 'Muse Audio Share'.
    // on Windows, any system audio device where deviceId is not overwritten to '__internal_muse_daw_bridge'.
    if (env.OS === 'darwin') {
        return media.label.match('Muse Audio Share') != null
    } else {
        return (
            media.label.match('Muse Audio Share') != null ||
            (isSystemAudio(media) && !media.deviceId.startsWith('__internal_muse_daw_bridge')) ||
            isVoicemeeterInput(media)
        )
    }
}

export function isBuiltInLoopback(media: { label: string }) {
    return media.label.match('Muse Multi') != null
}

export function isMuseRecordingDevice(media: AudioSourceInfo) {
    return media.label.match('Muse Recording') != null
}

export function isMuseDAWBridgeDevice(media: AudioSourceInfo) {
    if (env.OS === 'win32') {
        return media.deviceId.startsWith('__internal_muse_daw_bridge')
    } else {
        return media.label.match('Muse DAW Bridge') != null
    }
}

const regVoiceMeeterOutBN = /Voicemeeter Out B\d/
export function isVoicemeeterB(media: { label: string }) {
    return media.label.match(regVoiceMeeterOutBN) != null
}

export function isVoicemeeterInput(media: { label: string }) {
    return media.label.indexOf('Voicemeeter Input') >= 0
}

export function selectDefaultInputDevice(audioInputs: AudioSourceInfo[]) {
    for (let device of audioInputs) {
        if (device.label.match(/\(built-in\)/i) != null) {
            return device
        }
    }

    for (let device of audioInputs) {
        if (device.label.match(/microphone/i) != null) {
            return device
        }
    }

    for (let device of audioInputs) {
        // return first NOT containing:
        if (device.label.match(/\(virtual\)/i) == null) {
            return device
        }
    }

    // If still not found, then just return the first device.
    return audioInputs[0] || null
}

export function selectDefaultOutputDevice(audioOutputs: AudioSourceInfo[]) {
    for (let device of audioOutputs) {
        if (device.label.match(/\(built-in\)/i) != null) {
            return device
        }
    }

    for (let device of audioOutputs) {
        if (device.label.match(/(speaker)|(headphone)/i) != null) {
            return device
        }
    }

    for (let device of audioOutputs) {
        // return first NOT containing:
        if (device.label.match(/\(virtual\)/i) == null) {
            return device
        }
    }

    // If still not found, then just return the first device.
    return audioOutputs[0] || null
}

// From function TransportTypeToString() in "/media/audio/mac/core_audio_util_mac.cc"
// in Chromium source."
const transportTypes = [
    'Built-in',
    'Aggregate',
    'AutoAggregate',
    'Virtual',
    'PCI',
    'USB',
    'FireWire',
    'Bluetooth',
    'Bluetooth LE',
    'HDMI',
    'DisplayPort',
    'AirPlay',
    'AVB',
    'Thunderbolt',
]

export function convertAudioDeviceNameToOSCompatibleName(label: string) {
    // NOTE: handle this case better
    if (label.indexOf('(Built-in)') > 0) {
        label = 'Built-in Microphone'
    }

    // Remove the stuff in the parenthesis
    label = label.replace(' (Built-in)', '')
    label = label.replace(' (Virtual)', '')

    return label
}

export async function createTrackForMonoChannel(track: MediaStreamTrack, channel: number) {
    const audioContext = new window.AudioContext()
    const mediaStream = new MediaStream([track])

    const source = audioContext.createMediaStreamSource(mediaStream)
    if (channel >= source.channelCount) {
        audioContext.close()
        throw new Error(`Mono channel ${channel + 1} does not exist on this device.`)
    }
    const splitter = audioContext.createChannelSplitter(2)
    const merger = audioContext.createChannelMerger(1)
    const destination = audioContext.createMediaStreamDestination()

    source.connect(splitter)
    splitter.connect(merger, channel)
    merger.connect(destination)

    const newTrack = destination.stream.getAudioTracks()[0]
    return newTrack
}

// 1&2:
export function duplicateMediaStreamMonoChannelToStereo(
    track: MediaStreamTrack,
    channelSelectionCodeLegacy: ChannelSelectionCodeType
) {
    const endStream = new MediaStream([track])
    const context = new window.AudioContext()
    const microphone = context.createMediaStreamSource(endStream)

    // do the below channel copying for mono devices.
    // diagram:
    //                    -- 0, 0 -->
    // source -> splitter -- 0, 1 --> merger -> destination
    const splitter = context.createChannelSplitter(2)
    const merger = context.createChannelMerger(1)

    microphone.connect(splitter)

    if (channelSelectionCodeLegacy === '1') {
        // output 0 of the splitter node is connected to the input 0 of the merger node.
        splitter.connect(merger, 0, 0)
    } else if (channelSelectionCodeLegacy === '2') {
        // output 1 of the splitter node is connected to the input 0 of the merger node.
        splitter.connect(merger, 1, 0)
    } else {
        // output 0 of the splitter node is connected to the input 0 of the merger node.
        splitter.connect(merger, 0, 0)
        // output 1 of the splitter node is connected to the input 0 of the merger node as well.
        splitter.connect(merger, 1, 0)
    }

    const destination = context.createMediaStreamDestination()
    merger.connect(destination)

    const outputStream = destination.stream
    return outputStream.getAudioTracks()[0]
}
